import { formatDistance } from '../domain/measurement';
import { formatMetres } from '../elevation/elevationQuery';
import { nearestPointIndex, type ElevationProfile } from '../elevation/profile';

const SVG_NS = 'http://www.w3.org/2000/svg';
const MARGIN = { top: 12, right: 12, bottom: 22, left: 46 } as const;

/** "Nice" axis ticks (1-2-5 steps) covering [min, max]. */
export function niceTicks(min: number, max: number, count = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return [min];
  const raw = (max - min) / count; const power = 10 ** Math.floor(Math.log10(raw)); const fraction = raw / power;
  const step = (fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10) * power;
  const ticks: number[] = [];
  for (let value = Math.ceil(min / step) * step; value <= max + step * 1e-9; value += step) ticks.push(Number(value.toFixed(10)));
  return ticks;
}

/** Vertical domain with a little air and at least 4 m, so a flat profile is not drawn as a dramatic one. */
export function elevationDomain(min: number, max: number): [number, number] {
  const span = Math.max(max - min, 4); const pad = span * 0.1; const mid = (min + max) / 2;
  return [Math.floor(mid - span / 2 - pad), Math.ceil(mid + span / 2 + pad)];
}

const el = <K extends keyof SVGElementTagNameMap>(name: K, attributes: Record<string, string | number>, text?: string): SVGElementTagNameMap[K] => {
  const node = document.createElementNS(SVG_NS, name);
  Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, String(value)));
  if (text !== undefined) node.textContent = text;
  return node;
};

/** Index ranges [from, to] of consecutive samples without data. */
export function gapRanges(profile: ElevationProfile): [number, number][] {
  const gaps: [number, number][] = []; let start = -1;
  profile.points.forEach((point, index) => {
    if (point.elevation === null) { if (start < 0) start = index; } else if (start >= 0) { gaps.push([start, index - 1]); start = -1; }
  });
  if (start >= 0) gaps.push([start, profile.points.length - 1]);
  return gaps;
}

const axisDistance =(metres: number, total: number): string => total >= 2_000 ? `${(metres / 1_000).toLocaleString('es-NI', { maximumFractionDigits: 1 })} km` : `${Math.round(metres).toLocaleString('es-NI')} m`;

/**
 * Lightweight SVG chart: distance (m) × elevation (m). No chart library; redrawn only when the profile or the
 * container width changes. Pointer/touch scrubbing and arrow keys report the hovered sample index.
 */
export class ElevationProfileChart {
  private profile: ElevationProfile | null = null;
  private cursor: SVGGElement | null = null;
  private plot = { x: 0, y: 0, width: 0, height: 0 };
  private domain = { x: [0, 1] as [number, number], y: [0, 1] as [number, number] };
  private active = -1;
  private readonly observer: ResizeObserver | null;

  constructor(private readonly host: HTMLElement, private readonly onHover: (index: number | null) => void) {
    this.observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => this.draw());
    this.observer?.observe(host);
  }

  get hoveredIndex(): number { return this.active; }

  render(profile: ElevationProfile | null): void {
    this.profile = profile; this.active = -1;
    this.draw();
  }

  dispose(): void { this.observer?.disconnect(); this.host.replaceChildren(); this.cursor = null; }

  /** Highlights a sample (from the chart itself or programmatically, e.g. tests). */
  highlight(index: number | null): void {
    const profile = this.profile; const cursor = this.cursor;
    if (!profile || !cursor) return;
    const point = index !== null ? profile.points[index] : undefined;
    this.active = point ? index! : -1;
    if (!point) { cursor.setAttribute('visibility', 'hidden'); this.onHover(null); return; }
    const x = this.x(point.distance);
    cursor.setAttribute('visibility', 'visible');
    cursor.querySelector('line')!.setAttribute('x1', String(x)); cursor.querySelector('line')!.setAttribute('x2', String(x));
    const dot = cursor.querySelector('circle')!; const label = cursor.querySelector('text')!;
    if (point.elevation !== null) { dot.setAttribute('cx', String(x)); dot.setAttribute('cy', String(this.y(point.elevation))); dot.setAttribute('visibility', 'visible'); } else dot.setAttribute('visibility', 'hidden');
    label.textContent = `${formatDistance(point.distance)} · ${point.elevation !== null ? formatMetres(point.elevation) : 'Sin dato'}`;
    const right = x > this.plot.x + this.plot.width * 0.6;
    label.setAttribute('x', String(right ? x - 6 : x + 6)); label.setAttribute('text-anchor', right ? 'end' : 'start');
    this.onHover(this.active);
  }

  private x(distance: number): number { const [a, b] = this.domain.x; return this.plot.x + (distance - a) / (b - a || 1) * this.plot.width; }
  private y(elevation: number): number { const [a, b] = this.domain.y; return this.plot.y + this.plot.height - (elevation - a) / (b - a || 1) * this.plot.height; }

  private draw(): void {
    const profile = this.profile;
    const width = Math.max(240, Math.round(this.host.clientWidth || 600)); const height = Math.max(110, Math.round(this.host.clientHeight || 150));
    this.host.replaceChildren(); this.cursor = null;
    if (!profile || !profile.points.length) return;
    const { stats, points, runs } = profile;
    this.plot = { x: MARGIN.left, y: MARGIN.top, width: width - MARGIN.left - MARGIN.right, height: height - MARGIN.top - MARGIN.bottom };
    this.domain.x = [0, Math.max(stats.length, 1)];
    this.domain.y = stats.min && stats.max ? elevationDomain(stats.min.elevation, stats.max.elevation) : [0, 10];
    const svg = el('svg', { width, height, viewBox: `0 0 ${width} ${height}`, class: 'elevation-chart-svg', role: 'img', tabindex: 0, 'aria-label': `Perfil de elevación de ${formatDistance(stats.length)}` });
    const grid = el('g', { class: 'elevation-chart-grid' });
    niceTicks(this.domain.y[0], this.domain.y[1], 4).forEach((tick) => {
      const y = this.y(tick);
      grid.append(el('line', { x1: this.plot.x, x2: this.plot.x + this.plot.width, y1: y, y2: y }), el('text', { x: this.plot.x - 6, y: y + 3.5, 'text-anchor': 'end' }, `${tick.toLocaleString('es-NI')} m`));
    });
    niceTicks(0, stats.length, width < 420 ? 3 : 6).forEach((tick) => {
      const x = this.x(tick);
      grid.append(el('line', { x1: x, x2: x, y1: this.plot.y, y2: this.plot.y + this.plot.height, class: 'vertical' }), el('text', { x, y: height - 6, 'text-anchor': tick === 0 ? 'start' : 'middle' }, axisDistance(tick, stats.length)));
    });
    svg.append(grid);
    // Gaps (water / no data / outside coverage): shaded band, never a connecting line.
    const gaps = el('g', { class: 'elevation-chart-gaps' });
    gapRanges(profile).forEach(([from, to]) => {
      // The band spans from the last sample with data to the next one: that whole stretch is unknown.
      const x0 = this.x(points[Math.max(from - 1, 0)]!.distance); const x1 = this.x(points[Math.min(to + 1, points.length - 1)]!.distance);
      gaps.append(el('rect', { x: x0, y: this.plot.y, width: Math.max(1.5, x1 - x0), height: this.plot.height }));
    });
    svg.append(gaps);
    const base = this.plot.y + this.plot.height;
    runs.forEach(([from, to]) => {
      const run = points.slice(from, to + 1);
      const line = run.map((point, index) => `${index ? 'L' : 'M'}${this.x(point.distance).toFixed(1)},${this.y(point.elevation!).toFixed(1)}`).join('');
      if (run.length > 1) svg.append(el('path', { class: 'elevation-chart-area', d: `${line}L${this.x(run[run.length - 1]!.distance).toFixed(1)},${base}L${this.x(run[0]!.distance).toFixed(1)},${base}Z` }));
      svg.append(run.length > 1 ? el('path', { class: 'elevation-chart-line', d: line }) : el('circle', { class: 'elevation-chart-point', cx: this.x(run[0]!.distance), cy: this.y(run[0]!.elevation!), r: 2.5 }));
    });
    const extremes = el('g', { class: 'elevation-chart-extremes' });
    ([['max', stats.max], ['min', stats.min]] as const).forEach(([kind, extreme]) => {
      if (!extreme) return;
      const x = this.x(extreme.distance); const y = this.y(extreme.elevation);
      extremes.append(el('circle', { cx: x, cy: y, r: 3.5, class: kind }), el('text', { x: Math.min(Math.max(x, this.plot.x + 24), this.plot.x + this.plot.width - 24), y: kind === 'max' ? y - 6 : y + 13, 'text-anchor': 'middle', class: kind }, `${kind === 'max' ? 'Máx' : 'Mín'} ${formatMetres(extreme.elevation)}`));
    });
    svg.append(extremes);
    const cursor = el('g', { class: 'elevation-chart-cursor', visibility: 'hidden', 'pointer-events': 'none' });
    cursor.append(el('line', { x1: 0, x2: 0, y1: this.plot.y, y2: base }), el('circle', { cx: 0, cy: 0, r: 4.5 }), el('text', { x: 0, y: this.plot.y + 11 }));
    svg.append(cursor);
    const hit = el('rect', { x: this.plot.x, y: this.plot.y, width: this.plot.width, height: this.plot.height, class: 'elevation-chart-hit' });
    const pick = (event: PointerEvent): void => {
      const box = svg.getBoundingClientRect(); const scale = box.width ? width / box.width : 1;
      const x = (event.clientX - box.left) * scale;
      const distance = (x - this.plot.x) / (this.plot.width || 1) * (this.domain.x[1] - this.domain.x[0]);
      this.highlight(nearestPointIndex(profile, Math.min(Math.max(distance, 0), stats.length)));
    };
    hit.addEventListener('pointermove', pick);
    // Touch scrubbing must not focus the chart or start a page gesture.
    hit.addEventListener('pointerdown', (event) => { if (event.pointerType !== 'mouse') event.preventDefault(); pick(event); });
    hit.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse') this.highlight(null); });
    svg.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const next = this.active < 0 ? 0 : this.active + (event.key === 'ArrowRight' ? 1 : -1);
      this.highlight(Math.min(Math.max(next, 0), points.length - 1));
    });
    svg.append(hit);
    this.cursor = cursor;
    this.host.append(svg);
  }
}
